// Common Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  pagination?: PaginationInfo;
  timestamp: string;
  correlationId: string;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    /** Field-level validation errors keyed by camelCase field name */
    details?: Record<string, string>;
  };
  timestamp: string;
  correlationId: string;
}

export interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
}

export interface PaginationParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

// Auth Types
export interface Permission {
  id: number;
  code: string;
  name: string;
  description: string;
  module: string;
}

export interface RoleInfo {
  id: number;
  name: string;
  description: string;
}

export interface AuthUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: RoleInfo[];
  permissions: string[];
  allowedApps: string[];
}

export interface CompanyInfo {
  id: number;
  code: string;
  name: string;
  moduleCode: string;
  licenseType: string;
}

export interface AuthToken {
  token: string;
  expiresIn: number;
  user: AuthUser;
  company?: CompanyInfo;
}

// Keep legacy User type for backwards compatibility
export interface User extends AuthUser {
  isActive?: boolean;
  lastLogin?: string;
}

export interface UserRole {
  id: number;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: Permission[];
}

export interface RoleDetail extends UserRole {
  createdAt: string;
  userCount: number;
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  permissionIds: number[];
}

export interface UpdateRoleRequest {
  name: string;
  description: string;
  permissionIds: number[];
}

// Product Types
export interface ProductMaster {
  productId: string;
  productCode: string;
  productName: string;
  productDescription: string;
  categoryId: string;
  category?: ProductCategory;
  brand: string;
  taxCode: string;
  taxPercentage: number;
  unitOfMeasure: string;
  barcode: string;
  reorderLevel: number;
  isActive: boolean;
  remarks?: string;
  createdBy: string;
  createdDate: string;
  updatedBy?: string;
  updatedDate?: string;
}

export interface PurchaseItemMaster {
  purchaseItemId: string;
  productId: string;
  product?: ProductMaster;
  supplierId: string;
  supplier?: Supplier;
  supplierItemCode: string;
  purchasePrice: number;
  lastPurchasePrice?: number;
  currency: string;
  minimumOrderQuantity: number;
  maximumStockLevel?: number;
  leadTimeDays: number;
  batchTrackingEnabled: boolean;
  expiryTrackingEnabled: boolean;
  warehouseLocation: string;
  isSalesItem: boolean;
  createdBy: string;
  createdDate: string;
  updatedBy?: string;
  updatedDate?: string;
}

export interface SalesItemMaster {
  salesItemId: string;
  productId: string;
  product?: ProductMaster;
  sellingPrice: number;
  minimumSellingPrice?: number;
  discountAllowed: boolean;
  availableStock: number;
  profitMarginPercentage?: number;
  warrantyPeriod?: string;
  returnAllowed: boolean;
  returnPeriodDays: number;
  salesChannel: string;
  productImage?: string;
  createdBy: string;
  createdDate: string;
  updatedBy?: string;
  updatedDate?: string;
}

// Legacy Product Type (deprecated, kept for backwards compatibility)
export interface Product {
  id: number;
  sku: string;
  name: string;
  description: string;
  categoryId: number;
  categoryName?: string;
  unitOfMeasure: string;
  reorderLevel: number;
  reorderQuantity: number;
  attributeSetId?: number;
  isDeleted: boolean;
  createdAt: string;
  modifiedAt?: string;
}

// ── EAV attribute framework ──────────────────────────────────────────────────

export type AttributeDataType = 'Text' | 'Number' | 'Decimal' | 'Boolean' | 'Date' | 'Select';

export interface AttributeDefinition {
  id: number;
  code: string;
  name: string;
  description?: string;
  dataType: AttributeDataType;
  options?: string[];
  unit?: string;
  /** Module that seeded the definition; null/undefined = user-created. */
  moduleCode?: string;
  /** true = every product; false = only the categories in categoryIds. */
  appliesToAllProducts: boolean;
  isRequired: boolean;
  sortOrder: number;
  categoryIds: number[];
  categoryNames: string[];
  isActive: boolean;
}

export interface AttributeSetItem {
  id: number;
  attributeDefinitionId: number;
  code: string;
  name: string;
  dataType: AttributeDataType;
  options?: string[];
  unit?: string;
  sortOrder: number;
  isRequired: boolean;
}

export interface AttributeSet {
  id: number;
  code: string;
  name: string;
  description?: string;
  moduleCode?: string;
  isActive: boolean;
  items: AttributeSetItem[];
}

export interface ProductAttributeValue {
  attributeDefinitionId: number;
  code: string;
  name: string;
  dataType: AttributeDataType;
  options?: string[];
  unit?: string;
  isRequired: boolean;
  sortOrder: number;
  value?: string;
}

/** Attribute field on the product form, including its scope. */
export interface ProductAttributeField extends ProductAttributeValue {
  /** Custom attributes: true = every product, false = categoryIds only. Always true for module attributes. */
  appliesToAllProducts: boolean;
  categoryIds: number[];
}

export interface ProductAttributes {
  productId: number;
  /** Display title of the module block (e.g. "Pharmacy Product Attributes"). */
  moduleSetName?: string;
  moduleAttributes: ProductAttributeField[];
  customAttributes: ProductAttributeField[];
}

export interface ProductCategory {
  id: number;
  name: string;
  description?: string;
  parentCategoryId?: number;
  isActive: boolean;
  createdAt?: string;
  defaultInventoryAccountId?: number | null;
  defaultCOGSAccountId?: number | null;
  defaultRevenueAccountId?: number | null;
}

export interface ProductVariant {
  id: number;
  productId: number;
  variantName: string;
  variantValue: string;
  sku: string;
}

export interface ProductImage {
  id: number;
  productId: number;
  imageUrl: string;
  displayOrder: number;
}

// Stock Types
export interface StockLevel {
  id: number;
  productId: number;
  productName?: string;
  productSKU?: string;
  warehouseId: number;
  warehouseName?: string;
  warehouseZoneId?: number;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  reorderLevel?: number;
}

export interface StockMovement {
  id: number;
  productId: number;
  productName?: string;
  productSKU?: string;
  warehouseId?: number;
  warehouseName?: string;
  movementType: string;
  quantity: number;
  relatedDocumentType?: string;
  relatedDocumentId?: number;
  notes?: string;
  createdBy?: number;
  createdByName?: string;
  createdAt: string;
}

export interface StockAdjustment {
  id: number;
  productId: number;
  warehouseId: number;
  currentQuantity: number;
  newQuantity: number;
  reason: string;
  notes?: string;
  createdBy: number;
  createdAt: string;
}

// Warehouse Types
export interface Warehouse {
  id: number;
  code: string;
  name: string;
  location: string;
  isActive: boolean;
}

export interface WarehouseZone {
  id: number;
  warehouseId: number;
  zoneName: string;
  zoneType: string;
}

export interface WarehouseBin {
  id: number;
  warehouseZoneId: number;
  binCode: string;
  binLocation: string;
  capacity: number;
}

// Purchase Order Types
export interface PurchaseOrder {
  id: number;
  poNumber: string;
  supplierId: number;
  supplier?: Supplier;
  orderDate: string;
  expectedDeliveryDate: string;
  status: 'Draft' | 'Submitted' | 'Confirmed' | 'PartiallyReceived' | 'Received' | 'Closed';
  totalAmount: number;
  notes?: string;
  createdBy: number;
  createdAt: string;
}

export interface PurchaseOrderLine {
  id: number;
  purchaseOrderId: number;
  productId: number;
  product?: Product;
  quantity: number;
  receivedQuantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface ReceiptNote {
  id: number;
  purchaseOrderId: number;
  receiptNumber: string;
  receiptDate: string;
  warehouseId: number;
  notes?: string;
  createdBy: number;
}

// ─── Stock Transfer Types ─────────────────────────────────────────────────────

export type StockTransferStatus =
  | 'Draft'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'TransferredOut'
  | 'PartiallyReceived'
  | 'Completed'
  | 'Cancelled';

export type StockTransferApprovalStatus = 'Pending' | 'PendingApproval' | 'Approved' | 'Rejected';

export interface StockTransferItemDTO {
  id: number;
  productId: number;
  productName?: string;
  productCode?: string;
  unitId: number;
  unitName?: string;
  quantityRequested: number;
  quantityTransferred: number;
  quantityReceived: number;
  varianceQuantity: number;
  remarks?: string;
}

export interface StockTransferDTO {
  id: number;
  transferNumber: string;
  sourceWarehouseId: number;
  sourceWarehouseName?: string;
  destinationWarehouseId: number;
  destinationWarehouseName?: string;
  transferDate: string;
  expectedArrivalDate?: string;
  status: StockTransferStatus;
  approvalStatus: StockTransferApprovalStatus;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  notes?: string;
  rejectionNotes?: string;
  requestedByUserId: number;
  requestedByName?: string;
  transferredByUserId?: number;
  transferredByName?: string;
  transferredOutAt?: string;
  receivedByUserId?: number;
  receivedByName?: string;
  receivedAt?: string;
  createdAt: string;
  items: StockTransferItemDTO[];
}

export interface StockTransferListDTO {
  id: number;
  transferNumber: string;
  transferInNumber?: string;
  transferDate: string;
  expectedArrivalDate?: string;
  sourceWarehouseName?: string;
  destinationWarehouseName?: string;
  status: StockTransferStatus;
  approvalStatus: StockTransferApprovalStatus;
  itemCount: number;
  requestedByName?: string;
  createdAt: string;
}

export interface StockTransferApprovalItemDTO {
  id: number;
  stockTransferEntityId: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface StockTransferApprovalStatusDTO {
  id: number;
  transferNumber: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  approvalStatus: StockTransferApprovalStatus;
  approvals: StockTransferApprovalItemDTO[];
}

export interface StockTransferStatsDTO {
  totalTransfers: number;
  pendingApproval: number;
  approved: number;
  transferredOut: number;
  completed: number;
}

export interface CreateStockTransferItemRequest {
  productId: number;
  unitId: number;
  quantityRequested: number;
  remarks?: string;
}

export interface CreateStockTransferRequest {
  sourceWarehouseId: number;
  destinationWarehouseId: number;
  transferDate: string;
  expectedArrivalDate?: string;
  notes?: string;
  requestedByUserId: number;
  items: CreateStockTransferItemRequest[];
}

export interface TransferOutItemRequest {
  stockTransferItemId: number;
  quantityTransferred: number;
  remarks?: string;
}

export interface TransferInItemRequest {
  stockTransferItemId: number;
  quantityReceived: number;
  /** Unit the receiver counted in; omit or match item unit = no conversion. */
  receivedUnitId?: number;
  remarks?: string;
}

// Party Base Types
export enum PartyType {
  Supplier = 'Supplier',
  Customer = 'Customer',
}

export interface PartyBase {
  partyId: string;
  partyCode: string;
  partyName: string;
  partyType: PartyType;
  contactPersonName: string;
  contactNumber: string;
  alternateContactNumber?: string;
  emailAddress: string;
  website?: string;
  taxIdentificationNumber?: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  creditLimit: number;
  paymentTerms: string;
  currency: string;
  activeStatus: boolean;
  notes?: string;
  createdBy: string;
  createdDate: string;
  updatedBy?: string;
  updatedDate?: string;
}

// Supplier Types
export interface Supplier extends PartyBase {
  supplierId: string;
  supplierType: string;
  businessRegistrationNumber?: string;
  bankName?: string;
  bankAccountNumber?: string;
  swiftCode?: string;
  addressLine1: string;
  addressLine2?: string;
  warehousePickupAvailable: boolean;
  deliveryLeadTimeDays: number;
  supplierRating: number;
  preferredSupplier: boolean;
  contractStartDate: string;
  contractEndDate?: string;
  isBlacklisted: boolean;
  isApproved: boolean;
}

export interface SupplierDelivery {
  deliveryId: string;
  supplierId: string;
  purchaseOrderId: string;
  deliveryNumber: string;
  deliveryDate: string;
  expectedDeliveryDate: string;
  status: string;
  trackingNumber?: string;
  createdDate: string;
}

export interface SupplierStatement {
  supplierId: string;
  supplierName: string;
  fromDate: string;
  toDate: string;
  generatedDate: string;
  transactions: SupplierTransaction[];
  totalPurchases: number;
  totalPayments: number;
  balanceDue: number;
}

export interface SupplierTransaction {
  transactionId: string;
  transactionType: string;
  transactionDate: string;
  referenceNumber: string;
  amount: number;
}

// Customer Types
export interface Customer extends PartyBase {
  customerId: string;
  customerType: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  shippingAddressLine1: string;
  shippingAddressLine2?: string;
  preferredPaymentMethod: string;
  customerSegment: string;
  loyaltyPoints: number;
  discountPercentage: number;
  salesRepresentative?: string;
  customerRating: number;
  lastPurchaseDate?: string;
  outstandingBalance: number;
  isCreditBlocked: boolean;
}

export interface CustomerStatement {
  customerId: string;
  customerName: string;
  fromDate: string;
  toDate: string;
  generatedDate: string;
  transactions: CustomerTransaction[];
  totalSales: number;
  totalPayments: number;
  balanceDue: number;
}

export interface CustomerTransaction {
  transactionId: string;
  transactionType: string;
  transactionDate: string;
  referenceNumber: string;
  amount: number;
}

export interface SupplierPricing {
  id: number;
  supplierId: number;
  productId: number;
  unitPrice: number;
  minimumOrderQuantity: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

// Print Types
export enum PrintOutputType {
  PDF = 0,
  POS_PRINTER = 1,
  BOTH = 2
}

/** How each sales-bill line item is laid out. */
export enum BillItemLineFormat {
  ONE_LINE = 0,
  TWO_LINE = 1,
}

/** Paper the sales bill is designed for. */
export enum BillPaperSize {
  A4 = 0,
  POS_80 = 1,
}

export interface PrintResult {
  success: boolean;
  message: string;
  warnings: string[];
  pdfPath?: string;
  allowContinue: boolean;
}

export interface PrintHistory {
  id: number;
  documentType: string;
  documentId: number;
  documentReference: string;
  outputType: PrintOutputType;
  printerName?: string;
  success: boolean;
  errorMessage?: string;
  filePath?: string;
  printedAt: string;
  printedBy: number;
  createdDate: string;
}

// Company Settings Types
export interface CompanySettings {
  id: number;
  companyName: string;
  tinNo?: string;
  logoUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  currencyCode: string;
  financialYearStartMonth: number;
  enablePurchaseOrderApproval: boolean;
  defaultApprovalLevel: number;
  enableSalesInvoicePrinting: boolean;
  defaultPrintOutput: PrintOutputType;
  defaultPrinterName?: string;
  printerPageWidth: number;
  enableAutoSupplierCode: boolean;
  enableAutoCustomerCode: boolean;
  billShowLogo: boolean;
  billHeaderLine1?: string;
  billHeaderLine2?: string;
  billFooterLine1?: string;
  billFooterLine2?: string;
  billShowPaymentMethod: boolean;
  billShowTenderedAmount: boolean;
  billShowCompanyName: boolean;
  billShowAddress: boolean;
  billShowPhone: boolean;
  billShowEmail: boolean;
  billShowWebsite: boolean;
  billItemLineFormat: BillItemLineFormat;
  billShowItemCode: boolean;
  billShowQty: boolean;
  billShowUnitPrice: boolean;
  billShowLineTotal: boolean;
  billPaperSize: BillPaperSize;
  invoiceReferenceFieldEnabled: boolean;
  invoiceReferenceFieldLabel?: string | null;
  businessDate: string;
  createdDate: string;
  updatedDate?: string;
}

export interface UpdateCompanySettingsRequest {
  companyName: string;
  tinNo?: string;
  logoUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  currencyCode: string;
  financialYearStartMonth: number;
  enablePurchaseOrderApproval: boolean;
  defaultApprovalLevel: number;
  enableSalesInvoicePrinting: boolean;
  defaultPrintOutput: PrintOutputType;
  defaultPrinterName?: string;
  printerPageWidth: number;
  enableAutoSupplierCode: boolean;
  enableAutoCustomerCode: boolean;
  billShowLogo: boolean;
  billHeaderLine1?: string;
  billHeaderLine2?: string;
  billFooterLine1?: string;
  billFooterLine2?: string;
  billShowPaymentMethod: boolean;
  billShowTenderedAmount: boolean;
  billShowCompanyName: boolean;
  billShowAddress: boolean;
  billShowPhone: boolean;
  billShowEmail: boolean;
  billShowWebsite: boolean;
  billItemLineFormat: BillItemLineFormat;
  billShowItemCode: boolean;
  billShowQty: boolean;
  billShowUnitPrice: boolean;
  billShowLineTotal: boolean;
  billPaperSize: BillPaperSize;
}

// Audit Log Types
export interface AuditLog {
  id: number;
  entityType: string;
  entityId: number;
  action: 'Created' | 'Updated' | 'Deleted';
  userId: number;
  timestamp: string;
  changes?: Record<string, unknown>;
}

// ─── GRN Types ────────────────────────────────────────────────────────────────

export type GRNType = 'Direct' | 'WithPO';
export type GRNStatus = 'Draft' | 'Received' | 'Verified' | 'Completed';
export type GRNApprovalStatus = 'Pending' | 'PendingApproval' | 'PartiallyApproved' | 'Approved' | 'Rejected';

export interface GRNItemDTO {
  grnItemId: number;
  productId: number;
  productName?: string;
  productCode?: string;
  quantityOrdered: number;
  quantityReceived: number;
  unitId: number;
  unitName?: string;
  unitPrice: number;
  discountPercentage: number;
  taxPercentage: number;
  qualityStatus: string;
  batchNumber?: string;
  expiryDate?: string;
  manufactureDate?: string;
  remarks?: string;
  goodQuantity: number;
  defectiveQuantity: number;
}

export interface GRNDTO {
  grnId: number;
  grnNumber: string;
  grnDate: string;
  receiptDate: string;
  supplierId: number;
  supplierName?: string;
  warehouseId: number;
  warehouseName?: string;
  purchaseOrderId?: number;
  poNumber?: string;
  grnType: GRNType;
  status: GRNStatus;
  approvalStatus: GRNApprovalStatus;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  notes?: string;
  referenceNumber?: string;
  receivedByUserId: number;
  verifiedByUserId?: number;
  createdDate: string;
  grnItems: GRNItemDTO[];
}

export interface GRNApprovalDTO {
  id: number;
  grnId: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface GRNApprovalStatusDTO {
  grnId: number;
  grnNumber: string;
  totalValue: number;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  approvalStatus: GRNApprovalStatus;
  approvals: GRNApprovalDTO[];
}

export interface GRNListDTO {
  grnId: number;
  grnNumber: string;
  grnDate: string;
  supplierName?: string;
  warehouseName?: string;
  poNumber?: string;
  grnType: GRNType;
  status: GRNStatus;
  approvalStatus: GRNApprovalStatus;
  itemCount: number;
  totalQuantity: number;
}

export interface CreateGRNItemRequest {
  productId: number;
  purchaseOrderItemId?: number;
  quantityOrdered: number;
  quantityReceived: number;
  unitId: number;
  unitPrice: number;
  discountPercentage: number;
  taxPercentage: number;
  qualityStatus: string;
  batchNumber?: string;
  expiryDate?: string;
  manufactureDate?: string;
  remarks?: string;
  goodQuantity: number;
  defectiveQuantity: number;
}

export interface CreateGRNRequest {
  grnDate: string;
  receiptDate: string;
  supplierId: number;
  warehouseId: number;
  purchaseOrderId?: number;
  grnType: GRNType;
  paymentTypeId?: number;
  notes?: string;
  referenceNumber?: string;
  receivedByUserId: number;
  grnItems: CreateGRNItemRequest[];
}

export interface UpdateGRNItemRequest {
  grnItemId: number;
  quantityReceived: number;
  qualityStatus: string;
  batchNumber?: string;
  expiryDate?: string;
  manufactureDate?: string;
  remarks?: string;
  goodQuantity: number;
  defectiveQuantity: number;
}

export interface UpdateGRNRequest {
  receiptDate: string;
  warehouseId: number;
  notes?: string;
  status: string;
  receivedByUserId: number;
  verifiedByUserId?: number;
  grnItems: UpdateGRNItemRequest[];
}

export interface POLineForGRN {
  id: number;
  productId: number;
  productName?: string;
  productSKU?: string;
  quantity: number;
  receivedQuantity: number;
  pendingQuantity: number;
  unitPrice: number;
}

export interface POForGRN {
  purchaseOrderId: number;
  poNumber: string;
  orderDate: string;
  status: string;
  paymentTypeId?: number;
  paymentTypeName?: string;
  lines: POLineForGRN[];
}

// ─── Stock Reconciliation ────────────────────────────────────────────────────

export type StockReconciliationStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Posted' | 'Rejected' | 'Cancelled';
export type ReconciliationType = 'Daily' | 'Weekly' | 'Monthly' | 'Adhoc';

export interface StockReconciliationItemDTO {
  id: number;
  productId: number;
  productName?: string;
  productCode?: string;
  unitId: number;
  unitName?: string;
  systemQuantity: number;
  physicalQuantity: number;
  varianceQuantity: number;
  varianceReason?: string;
  notes?: string;
}

export interface StockReconciliationDTO {
  id: number;
  reconciliationNumber: string;
  warehouseId: number;
  warehouseName?: string;
  reconciliationDate: string;
  reconciliationType: string;
  status: StockReconciliationStatus;
  approvalStatus: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  notes?: string;
  rejectionNotes?: string;
  countedByUserId: number;
  countedByName?: string;
  postedByUserId?: number;
  postedByName?: string;
  postedAt?: string;
  createdAt: string;
  items: StockReconciliationItemDTO[];
}

export interface StockReconciliationListDTO {
  id: number;
  reconciliationNumber: string;
  reconciliationDate: string;
  reconciliationType: string;
  warehouseName?: string;
  status: StockReconciliationStatus;
  approvalStatus: string;
  itemCount: number;
  varianceItemCount: number;
  countedByName?: string;
  createdAt: string;
}

export interface StockReconciliationApprovalDTO {
  id: number;
  stockReconciliationEntityId: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface StockReconciliationApprovalStatusDTO {
  id: number;
  reconciliationNumber: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  approvalStatus: string;
  approvals: StockReconciliationApprovalDTO[];
}

export interface StockReconciliationStatsDTO {
  totalReconciliations: number;
  pendingApproval: number;
  approved: number;
  posted: number;
  withVariance: number;
}

export interface CreateStockReconciliationItemRequest {
  productId: number;
  unitId: number;
  physicalQuantity: number;
  varianceReason?: string;
  notes?: string;
}

export interface CreateStockReconciliationRequest {
  warehouseId: number;
  reconciliationDate: string;
  reconciliationType: string;
  notes?: string;
  countedByUserId: number;
  items: CreateStockReconciliationItemRequest[];
}

// ─── Damage Note Types ────────────────────────────────────────────────────────

export type DamageNoteStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Posted' | 'Rejected' | 'Cancelled';
export type DamageCategory = 'Breakage' | 'Expiry' | 'Spoilage' | 'Leakage' | 'OperationalDamage';

export interface DamageNoteItemDTO {
  id: number;
  productId: number;
  productName?: string;
  productCode?: string;
  unitId: number;
  unitName?: string;
  quantity: number;
  damageCategory: string;
  reasonNotes?: string;
  estimatedValue: number;
}

export interface DamageNoteDTO {
  id: number;
  damageNoteNumber: string;
  warehouseId: number;
  warehouseName?: string;
  damageDate: string;
  status: DamageNoteStatus;
  approvalStatus: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  notes?: string;
  rejectionNotes?: string;
  reportedByUserId: number;
  reportedByName?: string;
  postedByUserId?: number;
  postedByName?: string;
  postedAt?: string;
  createdAt: string;
  totalEstimatedValue: number;
  items: DamageNoteItemDTO[];
}

export interface DamageNoteListDTO {
  id: number;
  damageNoteNumber: string;
  damageDate: string;
  warehouseName?: string;
  status: DamageNoteStatus;
  approvalStatus: string;
  itemCount: number;
  totalEstimatedValue: number;
  reportedByName?: string;
  createdAt: string;
}

export interface DamageNoteApprovalDTO {
  id: number;
  damageNoteEntityId: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface DamageNoteApprovalStatusDTO {
  id: number;
  damageNoteNumber: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  approvalStatus: string;
  approvals: DamageNoteApprovalDTO[];
}

export interface DamageNoteStatsDTO {
  totalDamageNotes: number;
  pendingApproval: number;
  approved: number;
  posted: number;
  totalEstimatedLoss: number;
}

export interface CreateDamageNoteItemRequest {
  productId: number;
  unitId: number;
  quantity: number;
  damageCategory: string;
  reasonNotes?: string;
  estimatedValue: number;
}

export interface CreateDamageNoteRequest {
  warehouseId: number;
  damageDate: string;
  notes?: string;
  reportedByUserId: number;
  items: CreateDamageNoteItemRequest[];
}

// ─── Issue Note Types ─────────────────────────────────────────────────────────

export type IssueNoteStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Posted' | 'Rejected' | 'Cancelled';

export interface IssueNoteItemDTO {
  id: number;
  productId: number;
  productName?: string;
  productCode?: string;
  unitId: number;
  unitName?: string;
  quantity: number;
  purpose?: string;
  notes?: string;
}

export interface IssueNoteDTO {
  id: number;
  issueNoteNumber: string;
  warehouseId: number;
  warehouseName?: string;
  issueDate: string;
  status: IssueNoteStatus;
  approvalStatus: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  notes?: string;
  rejectionNotes?: string;
  issuedByUserId: number;
  issuedByName?: string;
  postedByUserId?: number;
  postedByName?: string;
  postedAt?: string;
  createdAt: string;
  items: IssueNoteItemDTO[];
}

export interface IssueNoteListDTO {
  id: number;
  issueNoteNumber: string;
  issueDate: string;
  warehouseName?: string;
  status: IssueNoteStatus;
  approvalStatus: string;
  itemCount: number;
  issuedByName?: string;
  createdAt: string;
}

export interface IssueNoteApprovalDTO {
  id: number;
  issueNoteEntityId: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface IssueNoteApprovalStatusDTO {
  id: number;
  issueNoteNumber: string;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  approvalStatus: string;
  approvals: IssueNoteApprovalDTO[];
}

export interface IssueNoteStatsDTO {
  totalIssueNotes: number;
  pendingApproval: number;
  approved: number;
  posted: number;
}

export interface CreateIssueNoteItemRequest {
  productId: number;
  unitId: number;
  quantity: number;
  purpose?: string;
  notes?: string;
}

export interface CreateIssueNoteRequest {
  warehouseId: number;
  issueDate: string;
  notes?: string;
  issuedByUserId: number;
  items: CreateIssueNoteItemRequest[];
}

// ─── Restaurant / Menu Types ──────────────────────────────────────────────────

export interface MenuItemTypeDTO {
  id: number;
  name: string;
  description?: string;
  iconName?: string;
  sortOrder: number;
  isActive: boolean;
  categoryCount?: number;
  createdAt: string;
}

export interface CreateMenuTypeDTO {
  name: string;
  description?: string;
  iconName?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface MenuItemRecipeDTO {
  id: number;
  menuItemId?: number;
  productId: number;
  productName?: string;
  productCode?: string;
  unitId: number;
  unitName?: string;
  quantity: number;
  unitCost: number;
  lineCost: number;
  notes?: string;
}

export interface CreateMenuItemRecipeDTO {
  productId: number;
  unitId: number;
  quantity: number;
  unitCost?: number;
  notes?: string;
}

export interface MenuItemModifierDTO {
  id: number;
  menuItemId: number;
  name: string;
  modifierType: 'AddOn' | 'Removal' | 'LevelSelection';
  additionalPrice: number;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface CreateMenuItemModifierDTO {
  menuItemId: number;
  name: string;
  modifierType: string;
  additionalPrice: number;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface MenuCategoryDTO {
  id: number;
  name: string;
  description?: string;
  menuItemTypeId: number;
  menuItemTypeName?: string;
  typeName?: string;
  sortOrder: number;
  isActive: boolean;
  itemCount?: number;
  createdAt: string;
}

export interface CreateMenuCategoryDTO {
  name: string;
  description?: string;
  menuItemTypeId: number;
  sortOrder?: number;
  isActive?: boolean;
}

export interface MenuItemListDTO {
  id: number;
  menuCode?: string;
  name: string;
  description?: string;
  menuCategoryId: number;
  menuCategoryName?: string;
  categoryName?: string;
  menuItemTypeName?: string;
  typeName?: string;
  price: number;
  imageUrl?: string;
  isAvailable: boolean;
  isActive: boolean;
  prepTimeMinutes?: number;
  sortOrder: number;
  kotBotType?: string;
  serviceType?: string;
  taxRate?: number;
  taxType?: string;
  isRecipeLocked?: boolean;
  manualCost?: number | null;
  createdAt: string;
}

export interface MenuItemDTO extends MenuItemListDTO {
  description?: string;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: number;
  foodCost?: number;
  grossProfit?: number;
  foodCostPercent?: number;
  recipes: MenuItemRecipeDTO[];
  modifiers?: MenuItemModifierDTO[];
}

export interface CreateMenuItemDTO {
  menuCode?: string;
  name: string;
  description?: string;
  menuCategoryId: number;
  price: number;
  imageUrl?: string;
  isAvailable?: boolean;
  isActive?: boolean;
  prepTimeMinutes?: number;
  sortOrder?: number;
  kotBotType?: string;
  serviceType?: string;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: number;
  taxRate?: number;
  taxType?: string;
  manualCost?: number | null;
  recipes: CreateMenuItemRecipeDTO[];
}

export interface MenuPackageListDTO {
  id: number;
  code?: string;
  name: string;
  price: number;
  isActive: boolean;
  sortOrder: number;
  itemCount: number;
  createdAt: string;
}

export interface MenuPackageItemDTO {
  id: number;
  menuPackageId: number;
  menuItemId: number;
  menuItemName: string;
  menuItemPrice: number;
  quantity: number;
  notes?: string;
}

export interface MenuPackageDTO {
  id: number;
  code?: string;
  name: string;
  description?: string;
  price: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  items: MenuPackageItemDTO[];
}

export interface RestaurantOrderLineDTO {
  id: number;
  menuItemId: number;
  itemName: string;
  unitPrice: number;
  quantity: number;
  sentQty: number;
  discountAmount: number;
  lineTotal: number;
  modifierNotes?: string;
  notes?: string;
}

export enum KotTicketType {
  KOT = 'KOT',
  BOT = 'BOT',
}

export interface RestaurantOrderListDTO {
  id: number;
  orderNumber: string;
  outletId?: number;
  outletName?: string;
  orderType: string;
  tableNumber?: string;
  numberOfPax?: number;
  customerName?: string;
  status: string;
  totalAmount: number;
  lineCount: number;
  createdAt: string;
}

export interface RestaurantOrderDTO {
  id: number;
  orderNumber: string;
  outletId?: number;
  outletName?: string;
  orderType: string;
  tableId?: number;
  tableNumber?: string;
  numberOfPax?: number;
  customerName?: string;
  status: string;
  subTotal: number;
  taxAmount: number;
  serviceChargeAmount: number;
  deliveryChargeAmount: number;
  discountAmount: number;
  totalAmount: number;
  paymentMethod?: string;
  amountPaid: number;
  changeGiven: number;
  notes?: string;
  /** Set when status is 'Voided' — the mandatory cancellation reason. */
  voidReason?: string;
  /** Generated at settlement — links to KOT tickets after bill is closed. */
  billNumber?: string;
  createdAt: string;
  modifiedAt?: string;
  /** True when at least one KOT/BOT ticket has been issued for this order. */
  kotIssued?: boolean;
  /** Issued KOT/BOT tickets — populated on the single-order endpoint only. */
  kotTickets?: KotTicketDTO[];
  lines: RestaurantOrderLineDTO[];
}

export interface KotTicketLineDTO {
  menuItemId: number;
  itemName: string;
  quantity: number;
  modifierNotes?: string;
  notes?: string;
}

export interface KotTicketDTO {
  id: number;
  ticketNumber: string;
  ticketType: string; // 'KOT' | 'BOT'
  ticketDate: string;
  printedAt: string;
  /** True for cancellation tickets issued when the order was voided. */
  isVoid?: boolean;
  lines: KotTicketLineDTO[];
}

export interface VoidOrderResponseDTO {
  orderId: number;
  orderNumber: string;
  /** Cancellation tickets to print for the kitchen/bar. */
  voidTickets: IssueKotResponseDTO[];
}

export interface CreateRestaurantOrderDTO {
  outletId?: number;
  orderType: string;
  tableId?: number;
  tableNumber?: string;
  numberOfPax?: number;
  customerName?: string;
  notes?: string;
  discountAmount?: number;
  paymentMethod?: string;
  amountPaid?: number;
  changeGiven?: number;
  lines: CreateRestaurantOrderLineDTO[];
}

export interface UpdateOrderLinesDTO {
  lines: CreateRestaurantOrderLineDTO[];
  discountAmount?: number;
}

export interface KotTicketLineDTO {
  menuItemId: number;
  itemName: string;
  quantity: number;
  modifierNotes?: string;
  notes?: string;
}

export interface IssueKotDTO {
  ticketType: string;
  lines: KotTicketLineDTO[];
}

export interface IssueKotResponseDTO {
  ticketId?: number;
  ticketNumber: string;
  ticketType: string;
  orderNumber: string;
  tableNumber: string;
  numberOfPax?: number;
  orderType: string;
  printedAt: string;
  lines: KotTicketLineDTO[];
  isVoid?: boolean;
  voidReason?: string;
}

export interface VoidKotItemsDTO {
  reason: string;
  items: KotTicketLineDTO[];
}

export interface VoidKotItemsResponseDTO {
  voidTickets: IssueKotResponseDTO[];
}

export interface BillHistoryItemDTO {
  id: number;
  billNumber?: string;
  orderNumber?: string;
  tableInfo?: string;
  itemCount: number;
  subTotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  amountPaid: number;
  changeGiven: number;
  paymentMethod?: string;
  status: string;
  voidReason?: string;
  settledAt?: string;
  voidedAt?: string;
}

export interface KotVoidReasonDTO {
  id: number;
  reason: string;
  isActive: boolean;
  sortOrder: number;
}

export interface SaveKotVoidReasonDTO {
  reason: string;
  isActive: boolean;
  sortOrder: number;
}

export interface CreateRestaurantOrderLineDTO {
  menuItemId: number;
  itemName: string;
  unitPrice: number;
  quantity: number;
  discountAmount?: number;
  modifierNotes?: string;
  notes?: string;
}

export interface OutletListDTO {
  id: number;
  code: string;
  name: string;
  address?: string;
  phone?: string;
  warehouseId?: number;
  warehouseName?: string;
  serviceChargePercent?: number;
  deliveryCharge?: number;
  isActive: boolean;
  createdAt: string;
}

export interface OutletDTO extends OutletListDTO {
  description?: string;
  email?: string;
  /** Local time-of-day (HH:mm) by which the previous trading day must be closed; null disables the guard. */
  dayCloseCutoff?: string | null;
}

export interface CreateOutletDTO {
  code: string;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  warehouseId?: number;
  serviceChargePercent?: number;
  deliveryCharge?: number;
  /** Local time-of-day (HH:mm) by which the previous trading day must be closed; empty disables the guard. */
  dayCloseCutoff?: string | null;
  isActive?: boolean;
}


// ── Happy Hour ────────────────────────────────────────────────────────────────

export interface HappyHourRuleListDTO {
  id: number;
  name: string;
  description?: string;
  activeDays: number;
  timeFrom: string;
  timeTo: string;
  discountType: 'Percentage' | 'FixedAmount';
  discountValue: number;
  appliesTo: 'AllItems' | 'SelectedCategories' | 'SelectedItems';
  isActive: boolean;
  createdAt: string;
  modifiedAt?: string;
}

export interface HappyHourRuleDTO extends HappyHourRuleListDTO {
  categoryIds: number[];
  categoryNames: string[];
  menuItemIds: number[];
  menuItemNames: string[];
}

export interface CreateHappyHourRuleDTO {
  name: string;
  description?: string;
  activeDays: number;
  timeFrom: string;
  timeTo: string;
  discountType: string;
  discountValue: number;
  appliesTo: string;
  isActive: boolean;
  categoryIds: number[];
  menuItemIds: number[];
}

export interface ActiveHappyHourDTO {
  id: number;
  name: string;
  discountType: string;
  discountValue: number;
  appliesTo: string;
  categoryIds: number[];
  menuItemIds: number[];
  timeFrom: string;
  timeTo: string;
}
